<template>
  <div id="survey-list" class="pa-4">
    <!-- Header Banner -->
    <div class="banner-header">
      <div class="banner-title">
        <h1 class="page-title">{{ PageTitle || "Survey" }}</h1>
        <p class="mt-2 mb-0 banner-subtitle">
          Create surveys, build questions, and review responses.
        </p>
      </div>
      <div class="d-flex align-center">
        <v-btn
          fab
          class="refresh-btn mr-3"
          @click="refreshPageData()"
          elevation="0"
        >
          <v-icon size="24">mdi-refresh</v-icon>
        </v-btn>
        <v-btn
          color="#8950FC"
          elevation="0"
          class="rounded-lg text-white"
          style="height: 44px; font-weight: 600; text-transform: none"
          @click.prevent="openCreateDialog"
        >
          <v-icon left>mdi-plus-circle-outline</v-icon>
          Create Survey
        </v-btn>
      </div>
    </div>

    <div class="d-flex justify-content-center mb-6" v-if="LoadingFlag">
      <b-spinner variant="primary" type="grow" label="Loading..."></b-spinner>
    </div>

    <div class="text-center py-10" v-if="!LoadingFlag && rows.length == 0">
      <h4 class="text-muted font-weight-bold">
        No surveys yet. Click "Create Survey" to get started.
      </h4>
    </div>

    <v-card
      class="result-card elevation-4 rounded-xl border-0"
      v-if="rows.length > 0"
    >
      <v-card-text class="pa-2 px-md-4">
        <v-data-table
          class="custom-table elevation-0"
          :loading="TableLoadingFlag"
          :headers="tableColumns1"
          :items="rows"
          :items-per-page="25"
        >
          <template v-slot:[`item.StatusTxt`]="{ item }">
            <v-chip :color="statusColor(item.Status)" small dark>{{
              item.StatusTxt
            }}</v-chip>
          </template>

          <template v-slot:[`item.Actions`]="{ item }">
            <div class="d-flex align-center" style="white-space: nowrap">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="mr-1"
                    @click="goQuestions(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="20" color="#8950FC"
                      >mdi-format-list-checks</v-icon
                    >
                  </v-btn>
                </template>
                <span>Questions</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="mr-1"
                    @click="goResults(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="20" color="#0097D7">mdi-chart-bar</v-icon>
                  </v-btn>
                </template>
                <span>Results</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.Status == 0">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="mr-1"
                    @click="openEditDialog(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="20" color="#EDBE38"
                      >mdi-file-document-edit</v-icon
                    >
                  </v-btn>
                </template>
                <span>Edit</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.Status == 0">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="mr-1"
                    @click="confirmStatus(item, 1)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="20" color="#56BDA3">mdi-publish</v-icon>
                  </v-btn>
                </template>
                <span>Publish</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.Status == 1">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="mr-1"
                    @click="confirmStatus(item, 2)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="20" color="#414042">mdi-lock</v-icon>
                  </v-btn>
                </template>
                <span>Close</span>
              </v-tooltip>

              <v-tooltip
                bottom
                v-if="item.Status == 1 && item.AllowMemberResponse"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="mr-1"
                    @click="copyMemberLink(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="20" color="#8950FC">mdi-account-key</v-icon>
                  </v-btn>
                </template>
                <span>Copy Member Link</span>
              </v-tooltip>

              <v-tooltip
                bottom
                v-if="item.Status == 1 && item.AllowPublicResponse"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="mr-1"
                    @click="copyPublicLink(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="20" color="#0097D7">mdi-link-variant</v-icon>
                  </v-btn>
                </template>
                <span>Copy Public Link</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.Status == 0">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    @click="confirmDelete(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="20" color="#EA5455">mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Delete</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Create / Edit Dialog -->
    <v-dialog v-model="dialogFlag" persistent max-width="700px">
      <v-card class="rounded-xl">
        <v-card-title class="px-6 py-4" style="background-color: #3a67b1">
          <span class="text-h6 font-weight-bold white--text">
            {{ EditId ? "Edit Survey" : "Create Survey" }}
          </span>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogFlag = false"
            ><v-icon color="#FFFFFF">mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text class="pa-6">
          <v-form ref="form1" v-model="valid1" lazy-validation>
            <v-row>
              <v-col cols="12">
                <label class="form-label"
                  >Title <span class="text-danger">*</span></label
                >
                <v-text-field
                  v-model="SurveyTitle"
                  :rules="SurveyTitleRules"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <label class="form-label">Description</label>
                <v-textarea
                  v-model="SurveyDescription"
                  outlined
                  dense
                  rows="3"
                ></v-textarea>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="form-label">Start Date</label>
                <v-menu
                  v-model="startMenu"
                  :close-on-content-click="false"
                  offset-y
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="StartDate"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="StartDate"
                    @input="startMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="form-label">End Date</label>
                <v-menu
                  v-model="endMenu"
                  :close-on-content-click="false"
                  offset-y
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="EndDate"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="EndDate"
                    @input="endMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-checkbox
                  v-model="AllowMemberResponse"
                  label="Allow logged-in members to respond"
                ></v-checkbox>
              </v-col>
              <v-col cols="12" sm="6">
                <v-checkbox
                  v-model="AllowPublicResponse"
                  label="Allow public (no login) responses via a link"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0 d-flex justify-end">
          <v-btn
            :disabled="!valid1"
            :loading="SubmitFlag"
            color="#56BDA3"
            class="rounded-lg font-weight-bold px-8 white--text"
            @click.prevent="submitForm"
          >
            Save
          </v-btn>
          <v-btn outlined class="rounded-lg mr-3" @click="dialogFlag = false"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  data() {
    return {
      LoadingFlag: false,
      TableLoadingFlag: false,
      SubmitFlag: false,

      rows: [],
      tableColumns1: [
        { text: "Title", value: "SurveyTitle" },
        { text: "Status", value: "StatusTxt" },
        { text: "Questions", value: "QuestionCount" },
        { text: "Responses", value: "ResponseCount" },
        { text: "Created", value: "CreatedAtTxt" },
        { text: "Actions", value: "Actions", sortable: false },
      ],

      dialogFlag: false,
      valid1: true,
      EditId: null,

      SurveyTitle: "",
      SurveyTitleRules: [(v) => !!v || "Title is required"],
      SurveyDescription: "",
      StartDate: "",
      EndDate: "",
      startMenu: false,
      endMenu: false,
      AllowMemberResponse: true,
      AllowPublicResponse: false,

      PageInfo: {},
      PageTitle: "",
    };
  },
  watch: {
    PageInfo: function () {
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      if (Object.keys(tr).length > 0) {
        if (!tr.PagePermissionFlag) {
          this.$router.push("/access-denied");
        }
        this.PageTitle = tr.PageTitle;
        this.getTableRecords();
      }
    },
  },
  methods: {
    refreshPageData() {
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = { UserInterface: 1, Module: "survey", Action: "list" };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    statusColor(status) {
      if (status == 1) return "#56BDA3";
      if (status == 2) return "#414042";
      return "#EDBE38";
    },
    getTableRecords() {
      this.TableLoadingFlag = true;
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/survey/lists";

      const thisIns = this;

      this.$http({
        url: add_url,
        method: "POST",
        data: { UserInterface: 1 },
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.TableLoadingFlag = false;
          var flag = response.data.flag;
          var records = response.data.records;
          if (flag == 1) {
            thisIns.rows = records.TableData;
          } else {
            thisIns.rows = [];
          }
        })
        .catch(function () {
          thisIns.TableLoadingFlag = false;
        });
    },
    openCreateDialog() {
      this.EditId = null;
      this.SurveyTitle = "";
      this.SurveyDescription = "";
      this.StartDate = "";
      this.EndDate = "";
      this.AllowMemberResponse = true;
      this.AllowPublicResponse = false;
      this.dialogFlag = true;
    },
    openEditDialog(item) {
      this.EditId = item.SurveyId;
      this.SurveyTitle = item.SurveyTitle;
      this.SurveyDescription = item.SurveyDescription;
      this.StartDate = item.StartDate || "";
      this.EndDate = item.EndDate || "";
      this.AllowMemberResponse = !!item.AllowMemberResponse;
      this.AllowPublicResponse = !!item.AllowPublicResponse;
      this.dialogFlag = true;
    },
    submitForm() {
      var validate1 = this.$refs.form1.validate();
      if (!validate1) {
        this.toast("error", "Kindly fill the required fields");
        return;
      }

      this.SubmitFlag = true;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url =
        server_url + (this.EditId ? "api/survey/update" : "api/survey/store");

      var upload = {
        SurveyId: this.EditId,
        SurveyTitle: this.SurveyTitle,
        SurveyDescription: this.SurveyDescription,
        StartDate: this.StartDate,
        EndDate: this.EndDate,
        AllowMemberResponse: this.AllowMemberResponse ? 1 : 0,
        AllowPublicResponse: this.AllowPublicResponse ? 1 : 0,
      };

      const thisIns = this;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.SubmitFlag = false;
          var output = response.data.output;
          var flag = response.data.flag;
          if (flag == 1) {
            thisIns.sweetAlert("success", output, false);
            thisIns.dialogFlag = false;
            thisIns.getTableRecords();
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function () {
          thisIns.SubmitFlag = false;
        });
    },
    confirmStatus(item, newStatus) {
      var actionTxt = newStatus == 1 ? "publish" : "close";
      const thisIns = this;
      Swal.fire({
        title: "Are you sure?",
        text:
          newStatus == 1
            ? "Once published, existing questions can no longer be edited (new ones can still be added)."
            : "Once closed, this survey will stop accepting new responses.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, " + actionTxt + " it",
        confirmButtonColor: "#56BDA3",
        cancelButtonColor: "#414042",
      }).then((result) => {
        if (result.isConfirmed) {
          thisIns.changeStatus(item, newStatus);
        }
      });
    },
    changeStatus(item, newStatus) {
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/survey/change-status";

      const thisIns = this;

      this.$http({
        url: add_url,
        method: "POST",
        data: { SurveyId: item.SurveyId, Status: newStatus },
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          var output = response.data.output;
          var flag = response.data.flag;
          if (flag == 1) {
            thisIns.sweetAlert("success", output, false);
            thisIns.getTableRecords();
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function () {});
    },
    confirmDelete(item) {
      const thisIns = this;
      Swal.fire({
        title: "Delete this survey?",
        text: "This cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it",
        confirmButtonColor: "#EA5455",
        cancelButtonColor: "#414042",
      }).then((result) => {
        if (result.isConfirmed) {
          thisIns.deleteSurvey(item);
        }
      });
    },
    deleteSurvey(item) {
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/survey/destroy";

      const thisIns = this;

      this.$http({
        url: add_url,
        method: "POST",
        data: { SurveyId: item.SurveyId },
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          var output = response.data.output;
          var flag = response.data.flag;
          if (flag == 1) {
            thisIns.sweetAlert("success", output, false);
            thisIns.getTableRecords();
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function () {});
    },
    goQuestions(item) {
      this.$router.push("/survey/questions/" + item.SurveyId);
    },
    goResults(item) {
      this.$router.push("/survey/results/" + item.SurveyId);
    },
    copyPublicLink(item) {
      var link =
        window.location.origin +
        window.location.pathname +
        "#/survey/public/" +
        item.PublicLinkCode;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(link);
      }
      this.sweetAlert("success", "Public link copied: " + link, false);
    },
    copyMemberLink(item) {
      var link =
        window.location.origin +
        window.location.pathname +
        "#/survey/take/" +
        item.SurveyId;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(link);
      }
      this.sweetAlert("success", "Member link copied: " + link, false);
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss" scoped>
#survey-list {
  .banner-header {
    background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%);
    border-radius: 20px;
    padding: 2rem 2.5rem;
    margin-bottom: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
  }
  .page-title {
    color: #ffffff;
    font-size: 2rem;
    font-weight: 700;
    margin: 0;
  }
  .banner-subtitle {
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
  }
  .refresh-btn {
    background: rgba(255, 255, 255, 0.15) !important;
    color: #ffffff !important;
  }
  .result-card {
    border-radius: 20px !important;
    box-shadow: 0 10px 40px rgba(10, 15, 41, 0.05) !important;
  }
  .form-label {
    font-size: 13px;
    font-weight: 700;
    color: #414042;
    text-transform: uppercase;
    display: block;
    margin-bottom: 6px;
  }
}
</style>
